import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTelegram } from './TelegramProvider';
import type { TgThemeParams } from '@/types/telegram';

export type ColorScheme = 'light' | 'dark';

interface ThemeContextValue {
  scheme: ColorScheme;
  params: TgThemeParams;
}

const ThemeContext = createContext<ThemeContextValue>({ scheme: 'light', params: {} });

const DARK_DEFAULTS: Required<TgThemeParams> = {
  bg_color: '#17212b',
  secondary_bg_color: '#0e1621',
  section_bg_color: '#17212b',
  text_color: '#ffffff',
  hint_color: '#7d8d9c',
  link_color: '#5eb5f7',
  button_color: '#3390ec',
  button_text_color: '#ffffff',
  destructive_text_color: '#ec5c5c',
  section_separator_color: 'rgba(255,255,255,0.08)',
};

const LIGHT_DEFAULTS: Required<TgThemeParams> = {
  bg_color: '#ffffff',
  secondary_bg_color: '#f4f4f5',
  section_bg_color: '#ffffff',
  text_color: '#000000',
  hint_color: '#707579',
  link_color: '#2481cc',
  button_color: '#2481cc',
  button_text_color: '#ffffff',
  destructive_text_color: '#df3f40',
  section_separator_color: 'rgba(0,0,0,0.08)',
};

/** Применяет цвета темы к корневым CSS-переменным. */
function applyTheme(scheme: ColorScheme, params: TgThemeParams): void {
  const merged = { ...(scheme === 'dark' ? DARK_DEFAULTS : LIGHT_DEFAULTS), ...params };
  const root = document.documentElement;
  root.style.setProperty('--tg-bg', merged.bg_color);
  root.style.setProperty('--tg-secondary-bg', merged.secondary_bg_color);
  root.style.setProperty('--tg-section-bg', merged.section_bg_color);
  root.style.setProperty('--tg-text', merged.text_color);
  root.style.setProperty('--tg-hint', merged.hint_color);
  root.style.setProperty('--tg-link', merged.link_color);
  root.style.setProperty('--tg-button', merged.button_color);
  root.style.setProperty('--tg-button-text', merged.button_text_color);
  root.style.setProperty('--tg-destructive', merged.destructive_text_color);
  root.style.setProperty('--tg-separator', merged.section_separator_color);
  document.documentElement.dataset.theme = scheme;
}

/**
 * Слушает события `themeChanged` от Telegram и применяет цвета темы.
 * Если запущено вне Telegram — использует системную prefers-color-scheme.
 */
export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const { webApp } = useTelegram();
  const [scheme, setScheme] = useState<ColorScheme>(() => {
    if (webApp) return webApp.colorScheme;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [params, setParams] = useState<TgThemeParams>(() => webApp?.themeParams ?? {});

  useEffect(() => {
    if (!webApp) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (): void => setScheme(mq.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    const onChange = (): void => {
      setScheme(webApp.colorScheme);
      setParams({ ...webApp.themeParams });
    };
    webApp.onEvent('themeChanged', onChange);
    return () => webApp.offEvent('themeChanged', onChange);
  }, [webApp]);

  useEffect(() => {
    applyTheme(scheme, params);
  }, [scheme, params]);

  const value = useMemo(() => ({ scheme, params }), [scheme, params]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Хук для получения текущей темы Telegram. */
export function useThemeParams(): ThemeContextValue {
  return useContext(ThemeContext);
}
